import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  GraduationCap, UsersThree, ChatCircleDots, CalendarStar, FileText,
  QrCode, MagnifyingGlass, Heart, ArrowRight, Buildings, Newspaper, ArrowUpRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const features = [
  { icon: GraduationCap, slug: "education-timeline", title: "Full education timeline", body: "Record every institution, campus, department, course, class & batch — from kindergarten to grad school." },
  { icon: ChatCircleDots, slug: "community-chat", title: "A chat room per community", body: "Every class, batch and reunion group gets its own persistent chat, auto-created with the group." },
  { icon: MagnifyingGlass, slug: "find-a-classmate", title: "Find a classmate", body: "Privacy-first advanced search across a global alumni directory. Reconnect with anyone." },
  { icon: FileText, slug: "resume-builder", title: "AI resume builder", body: "Auto-draft a polished resume from your profile, import your LinkedIn PDF, export & share." },
  { icon: CalendarStar, slug: "events-reunions", title: "Events & reunions", body: "Host reunions with RSVP, guest lists and virtual links. Never miss a gathering again." },
  { icon: QrCode, slug: "splink-qr", title: "SpLink short URLs + QR Codes", body: "Every profile, page & resume gets a branded short link and QR code, ready to share." },
];

const logos = ["MIT", "Oxford", "St. Stephen's", "GMEC", "Springfield High", "IIT Bombay", "Stanford", "Cambridge"];

export default function Landing() {
  const [news, setNews] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/news").then((r) => setNews((r.data.items || []).slice(0, 6))).catch(() => {}); }, []);
  const findInstitution = (e) => { e.preventDefault(); nav(q.trim() ? `/explore/institutions?q=${encodeURIComponent(q.trim())}` : "/explore/institutions"); };
  const ctas = [
    { to: "/explore/institutions", icon: Buildings, title: "Find My Institution", body: "Search schools, colleges and universities worldwide.", primary: true },
    { to: "/find", icon: MagnifyingGlass, title: "Find a Classmate", body: "Search privately by institution, batch, course and name." },
    { to: "/groups", icon: UsersThree, title: "Create an Alumni Group", body: "Bring a class, batch, faculty or staff community together." },
  ];
  return (
    <div className="bg-background">
      {/* Top tagline banner */}
      <section className="border-b border-border/60 bg-primary/[0.03]" data-testid="home-tagline">
        <div className="mx-auto max-w-7xl px-5 py-4 text-center">
          <p className="font-heading text-lg font-semibold tracking-tight sm:text-xl">
            AlumniPulse™ — <span className="text-secondary">Every Institution. Every Generation. One Global Community.</span>
          </p>
          <p className="mx-auto mt-1.5 max-w-3xl text-sm font-medium text-foreground/90 sm:text-[15px]">
            Connecting schools, colleges, universities, academic institutions, students, teachers and alumni — wherever they are in the world.
          </p>
        </div>
      </section>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-16 lg:grid-cols-12 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-6">
            <p className="overline text-secondary mb-5">A digital home for memories</p>
            <h1 className="font-heading text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Where Your Journey <span className="italic font-medium text-secondary">Continues.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Reconnect with the people, places, and memories that shaped your life. Build your complete educational story, find old classmates and friends, preserve the moments you shared, and stay connected with your school and alumni community — wherever life takes you.
            </p>
            <p className="mt-4 max-w-xl font-heading text-base font-medium text-foreground sm:text-lg">
              Which school, college or university shaped your life?
            </p>
            <form onSubmit={findInstitution} className="mt-8" data-testid="hero-institution-search">
              <div className="flex flex-col gap-2.5 sm:flex-row">
                <div className="relative flex-1">
                  <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing your institution…"
                    className="w-full rounded-full border border-border bg-card py-3.5 pl-12 pr-4 text-base shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    data-testid="hero-search-input" />
                </div>
                <Button type="submit" size="lg" className="rounded-full gap-2 px-7 active:scale-95 transition-transform" data-testid="hero-search-btn">
                  Find My Institution <ArrowRight size={18} />
                </Button>
              </div>
            </form>
            <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
              <Heart weight="fill" className="text-secondary" size={16} /> Free to join • No credit card • Control who can find and contact you
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Already a member? <Link to="/login" className="font-medium text-secondary hover:underline" data-testid="hero-signin-link">Sign in</Link></p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-3 -rotate-2 rounded-[2rem] bg-secondary/10" />
              <img src="https://images.pexels.com/photos/8199169/pexels-photo-8199169.jpeg"
                alt="Diverse group of graduates celebrating together on campus"
                className="relative aspect-[4/3] w-full rounded-[1.75rem] object-cover shadow-xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Primary CTAs */}
      <section className="mx-auto max-w-7xl px-5 pb-4 pt-2" data-testid="home-ctas">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {ctas.map((c, i) => (
            <motion.div key={c.to} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
              <Link to={c.to} data-testid={`home-cta-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group flex h-full flex-col rounded-2xl border p-7 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-md ${c.primary ? "border-secondary/40 bg-secondary/[0.04]" : "border-border/60 bg-card"}`}>
                <span className="mb-5 inline-grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-secondary group-hover:text-secondary-foreground">
                  <c.icon size={22} weight="fill" />
                </span>
                <h3 className="font-heading text-xl font-medium">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary">Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Logo marquee */}
      <section className="border-y border-border/60 bg-muted/40 py-6 overflow-hidden" data-testid="home-marquee">
        <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Search institutions worldwide, including…</p>
        <div className="flex w-max animate-marquee gap-14 px-6">
          {[...logos, ...logos].map((l, i) => (
            <span key={i} className="font-heading text-lg font-medium text-muted-foreground/80 whitespace-nowrap flex items-center gap-2">
              <Buildings size={18} /> {l}
            </span>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-5 py-20">
        <div className="max-w-2xl">
          <p className="overline text-secondary mb-3">Everything in one place</p>
          <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">The complete alumni ecosystem, thoughtfully built.</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.article key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}>
              <Link to={`/features/${f.slug}`} data-testid={`feature-card-${f.slug}`}
                className="group flex h-full flex-col rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-colors hover:border-secondary/40">
                <span className="mb-5 inline-grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-secondary group-hover:text-secondary-foreground">
                  <f.icon size={24} weight="duotone" />
                </span>
                <h3 className="font-heading text-xl font-medium">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary opacity-0 transition-opacity group-hover:opacity-100">Learn more <ArrowRight size={14} /></span>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Brand storyboard band */}
      <section className="mx-auto max-w-7xl px-5 py-10" data-testid="landing-storyboard">
        <div className="overflow-hidden rounded-[2rem] border border-border/60 bg-card shadow-sm">
          <img src="/storyboard1.png" alt="AlumniPulse — Where connections last a lifetime. Find friends & classmates, record your education, preserve memories, build global communities." className="h-auto w-full" loading="lazy" />
        </div>
      </section>


      {/* Latest news strip */}
      {news.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-6">
          <div className="rounded-[2rem] border border-border/60 bg-card p-6 sm:p-8">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl font-medium"><Newspaper size={22} weight="duotone" className="text-secondary" /> Latest education & alumni news</h2>
              <Link to="/news" className="flex items-center gap-1 text-sm text-secondary hover:underline">See all <ArrowRight size={14} /></Link>
            </div>
            <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {news.map((n) => (
                <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="group flex items-start justify-between gap-3 border-b border-border/50 py-2.5" data-testid="landing-news-item">
                  <span className="min-w-0"><span className="mr-2 text-xs font-medium text-secondary">{n.source}</span><span className="text-sm transition-colors group-hover:text-secondary">{n.title}</span></span>
                  <ArrowUpRight size={15} className="mt-0.5 shrink-0 text-muted-foreground transition-colors group-hover:text-secondary" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Institution website builder + live demo */}
      <section className="mx-auto max-w-7xl px-5 pb-4 pt-4">
        <div className="grid items-center gap-10 rounded-[2rem] border border-border/60 bg-gradient-to-br from-accent/40 to-background p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <p className="overline text-secondary mb-3">For institutions</p>
            <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Create your institution's website — in minutes.</h2>
            <p className="mt-4 text-muted-foreground">Classify your institution, pick modules and a theme, and let AI draft a professional, multilingual public website. Publish it, share it with a QR code, and grow your alumni community.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/try"><Button size="lg" className="rounded-full gap-2 px-7" data-testid="cta-try-builder">Try it free — no signup <ArrowRight size={18} /></Button></Link>
              <Link to="/site/demo-northgate-university"><Button size="lg" variant="outline" className="rounded-full gap-2 px-7" data-testid="cta-view-demo"><Buildings size={18} /> See a live demo</Button></Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 rotate-2 rounded-[2rem] bg-secondary/10" />
            <img src="https://images.pexels.com/photos/159775/library-la-trobe-study-students-159775.jpeg" alt="A university campus website example" className="relative aspect-[4/3] w-full rounded-[1.75rem] object-cover shadow-xl" />
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="mx-auto max-w-7xl px-5 pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-primary px-8 py-16 text-primary-foreground grain sm:px-16">
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Your class is waiting. Come home.</h2>
            <p className="mt-4 text-primary-foreground/70">Join a growing global community of alumni preserving their stories together.</p>
            <Link to="/register"><Button size="lg" variant="secondary" className="mt-8 rounded-full gap-2 px-7" data-testid="cta-bottom">Create your free profile <ArrowRight size={18} /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
